import {minimatch} from 'minimatch';

export function getAllPaths(obj: object, options: {skip?: string[]; onlyLeaf?: boolean} = {}): string[] {
  const {skip = [], onlyLeaf = false} = options;

  function recursiveGetPaths(value: object | string | number, prefix: string): string[] {
    const paths: string[] = [];

    if (typeof value === 'object' && value !== null) {
      if (Array.isArray(value)) {
        for (let i = 0; i < value.length; i++) {
          paths.push(...recursiveGetPaths(value[i], `${prefix ? `${prefix}.${i}` : i}`));
        }
      } else {
        for (const key of Object.keys(value)) {
          if (skip.includes(key)) {
            if (prefix) {
              paths.push(prefix);
            }
            continue;
          }
          paths.push(...recursiveGetPaths(value[key as keyof typeof value], prefix ? `${prefix}.${key}` : key));
        }
      }
      if (!onlyLeaf && prefix) {
        paths.push(prefix);
      }
    } else if (typeof value === 'string' || typeof value === 'number') {
      paths.push(prefix);
    }

    return paths;
  }

  return recursiveGetPaths(obj, '');
}

const isGlobPattern = (path: string) => {
  return path.includes('*') || path.includes('?') || path.includes('[') || path.includes(']');
};

/**
 * Returns all paths in the object that match the glob pattern.
 * If the path does not contain a glob pattern, it returns the path as single element.
 *
 * @param obj Object to match the path against
 * @param path Field to match, can be a glob pattern (e.g. "a.b.*")
 * @returns Array of matching paths
 */
export const globMatchPath = (obj: object, path: string) => {
  if (isGlobPattern(path)) {
    // Escape brackets from some[test] to some\[test\] to match literally
    const escapedPath = path.replaceAll(/\[([^\]]+)\]/g, '\\[$1\\]');

    return getAllPaths(obj, {skip: ['$ref'], onlyLeaf: true}).filter(
      minimatch.filter(escapedPath, {
        nocomment: true,
        noglobstar: true,
        dot: true,
        nobrace: true,
        noext: true,
        nonegate: true,
      }),
    );
  }

  return [path];
};

/**
 * Apply the defined function for each path found for the field in the object
 * @param obj Object to match the path against
 * @param path Field to match, can be a glob pattern (e.g. "a.b.*")
 * @param fn Function to apply to each match
 */
export const applyForField = <R>(obj: object, path: string, fn: (matchedPath: string) => R): R[] => {
  return globMatchPath(obj, path).map(path => fn(path));
};
